import { invert, mapValues, startCase } from "lodash"
import { GAME_KEYS } from "@/store/game/game.constants"

export const GAME_PLATFORMS = {
  PC: "pc",
  MOBILE: "mobile",
  IOS: "ios",
  ANDROID: "android",
  CONSOLE: "console",
  PS4: "ps4",
  PS5: "ps5",
  XBOX: "xbox",
  XBOX_ONE: "xbox_one",
  XBOX_SERIES_X: "xbox_series_x",
  SWITCH: "switch",
  STADIA: "stadia",
}

export const PLATFORM_NAMES = platform => {
  switch (platform) {
    case GAME_PLATFORMS.PC:
      return "PC"
    case GAME_PLATFORMS.MOBILE:
      return "Mobile"
    case GAME_PLATFORMS.IOS:
      return "iOS"
    case GAME_PLATFORMS.ANDROID:
      return "Android"
    case GAME_PLATFORMS.CONSOLE:
      return "Console"
    case GAME_PLATFORMS.PS4:
      return "PS4"
    case GAME_PLATFORMS.PS5:
      return "PS5"
    case GAME_PLATFORMS.XBOX:
      return "Xbox"
    case GAME_PLATFORMS.XBOX_ONE:
      return "Xbox One"
    case GAME_PLATFORMS.XBOX_SERIES_X:
      return "Xbox Series X"
    case GAME_PLATFORMS.SWITCH:
      return "Nintendo Switch"
    case GAME_PLATFORMS.STADIA:
      return "Google Stadia"
    default:
      return startCase(platform)
  }
}

export const GAME_PLATFORM_LABELS = mapValues(
  invert(GAME_PLATFORMS),
  (value, platform) => PLATFORM_NAMES(platform)
)

export const GAME_USERNAME_LABELS = mapValues(
  invert(GAME_KEYS),
  (value, gameKey) => {
    switch (gameKey) {
      case GAME_KEYS.LEAGUE_OF_LEGENDS:
        return "Summoner Name"
      case GAME_KEYS.FORTNITE:
        return "Fortnite Username"
      case GAME_KEYS.CSGO:
        return "Steam Username"
      case GAME_KEYS.SSBU:
        return "Smash Username"
      case GAME_KEYS.TEAMFIGHT_TACTICS:
        return "Summoner Name"
      case GAME_KEYS.OVERWATCH:
        return "Battlenet Username"
      case GAME_KEYS.HEARTHSTONE:
        return "Battlenet Username"
      default:
        return "Username"
    }
  }
)

export const COACH_CONNECTIVITY_STATUSES = {
  ONLINE: "online",
  OFFLINE: "offline",
}

export const COACH_ACTIVITY_STATUSES = {
  IDLE: "idle",
  BUSY: "busy",
  IN_SESSION: "in-session",
}

export const SESSION_REQUEST_STATUSES = {
  ACCEPTED: "accepted",
  REJECTED: "rejected",
  OPEN: "open",
  IN_SESSION: "in-session",
  FINISHED: "finished",
  CLOSED: "closed",
}

export const SESSION_REQUEST_ASYNC_STATUSES = {
  REQUESTED: "requested",
  VALIDATING: "validating",
  AUTHORIZING: "authorizing",
  ENQUEUED: "enqueued",
  LABORING: "laboring",
  APPEALED: "appealed",
  COMPLETED: "completed",
  REJECTED: "rejected",
  CLOSED: "closed",
}

export const SESSION_TYPES = {
  IN_GAME: "in-game",
  SPECTATE: "spectate",
  REPLAY_ANALYSIS: "replay",
  ONE_VS_ONE: "1v1",
  TWO_HOUR_SESSION: "2-hour",
  THREE_HOUR_SESSION: "3-hour",
  TWO_WINS_BUNDLE: "2-wins-bundle",
  FIVE_WINS_BUNDLE: "5-wins-bundle",
  ASYNC_VOD_REVIEW: "async-vod-review",
  DECK_BUILDING: "deck-building",
  SPECTATE_TCG_DRAFT: "spectate-tcg-draft",
  SPECTATE_TCG_EVENT: "spectate-tcg-event",
  GENERAL_COACHING: "general-coaching",
}

export const SESSION_TYPE_LABELS = mapValues(
  invert(SESSION_TYPES),
  (value, sessionType) => {
    switch (sessionType) {
      case SESSION_TYPES.IN_GAME:
        return "In-Game Coaching"
      case SESSION_TYPES.SPECTATE:
        return "In-Game Spectating"
      case SESSION_TYPES.REPLAY_ANALYSIS:
        return "Replay Analysis"
      case SESSION_TYPES.ONE_VS_ONE:
        return "1v1 Coaching"
      case SESSION_TYPES.TWO_HOUR_SESSION:
        return "2 Hour Session"
      case SESSION_TYPES.THREE_HOUR_SESSION:
        return "3 Hour Session"
      case SESSION_TYPES.TWO_WINS_BUNDLE:
        return "2-GAME SESSION"
      case SESSION_TYPES.FIVE_WINS_BUNDLE:
        return "5-GAME SESSION"
      case SESSION_TYPES.ASYNC_VOD_REVIEW:
        return "Pro Video Review"
      case SESSION_TYPES.DECK_BUILDING:
        return "Deck Building"
      case SESSION_TYPES.SPECTATE_TCG_DRAFT:
        return "Draft Spectating"
      case SESSION_TYPES.SPECTATE_TCG_EVENT:
        return "Constructed Event Spectating"
      case SESSION_TYPES.GENERAL_COACHING:
        return "General Coaching"
      default:
        return "Coaching"
    }
  }
)

export const SESSION_TYPE_COACHING_DESCRIPTIONS = mapValues(
  invert(SESSION_TYPES),
  (value, sessionType) => {
    switch (sessionType) {
      case SESSION_TYPES.IN_GAME:
        return "Coaching while playing a game with a student."
      case SESSION_TYPES.SPECTATE:
        return "Giving feedback while watching the student play."
      case SESSION_TYPES.REPLAY_ANALYSIS:
        return "Giving feedback for pre-recorded gameplay."
      case SESSION_TYPES.ONE_VS_ONE:
        return "Coaching while playing 1v1 with the student."
      case SESSION_TYPES.DECK_BUILDING:
        return "Building a deck with a student, covering deck basics and execution, and coaching while testing out the deck."
      case SESSION_TYPES.SPECTATE_TCG_DRAFT:
        return "Drafting a deck with a student and coaching while watching them play."
      case SESSION_TYPES.SPECTATE_TCG_EVENT:
        return "Building a deck with a student for a specific event and coaching while watching them play."
      case SESSION_TYPES.GENERAL_COACHING:
        return "Customized coaching session based on their learning goals."
    }
  }
)

export const SESSION_TYPE_NAME = {
  "in-game": "Play with Pro",
  "all-day-pass": "All Day Pass",
  "spectate": "In Game Spectate",
  "replay": "Replay Analysis",
  "1v1": "1v1 Coaching",
}

export const TAXONOMY_LABEL_BY_GAME = (key, game) => {
  const rankGames = [
    "leagueoflegends",
    "valorant",
    "teamfight-tactics",
    "rocket-league",
    "apex-legends",
    "overwatch",
    "wild-rift",
  ]
  const roleGames = ["leagueoflegends", "dota2"]
  const championGames = ["leagueoflegends"]
  switch (key) {
    case "ACHIEVEMENTS":
      if (rankGames.includes(game)) {
        return "RANK"
      }
      return key
    case "SPECIALIZATIONS":
      if (roleGames.includes(game)) {
        return "ROLES"
      }
      return key
    case "SPECIALIZATION_ITEMS":
      if (championGames.includes(game)) {
        return "CHAMPIONS"
      }
      return "MAINS"
    case "STATUS":
      return "AVAILABILITY"
    default:
      return key
  }
}

export const TAXONOMY_OPTION = option => {
  switch (option) {
    case "pc":
      return "PC"
    case "console":
      return "CONSOLE"
    case "ps4":
      return "PS4"
    case "xbox":
      return "XBOX"
    case "switch":
      return "NINTENDO SWITCH"
    case "stadia":
      return "GOOGLE STADIA"
    case "online":
      return "Now"
    case "offline":
      return "Later"
    case "ad":
      return "Ad"
    case "jungle":
      return "Jungle"
    case "top":
      return "Top"
    case "mid":
      return "Mid"
    case "support":
      return "Support"
    default:
      return option
  }
}

export const COACHING_STEP = {
  INIT: "INIT",
  SCHEDULE: "SCHEDULE",
  SELECTING: "SELECTING",
  IN_SESSION: "IN_SESSION",
  REFILL: "REFILL",
  FILL_CONTACT: "FILL_CONTACT",
  SUBSCRIBED_TO_COACH: "SUBSCRIBED_TO_COACH",
  PENDING: "PENDING",
  FAIL: "FAIL",
  REVIEW: "REVIEW",
  SUBSCRIBED: "SUBSCRIBED",
  SCHEDULED: "SCHEDULED",
}

export const SERVICE_TYPE = {
  INSTANT_SESSION: "INSTANT_SESSION",
  SUBSCRIPTION: "SUBSCRIPTION",
  SCHEDULE_SESSION: "SCHEDULE_SESSION",
}

export const AUTH_STEP = {
  LOGIN: "LOGIN",
  REGISTER: "REGISTER",
}



// WEBPACK FOOTER //
// ./src/constants/coaching.js